import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import type {
  Project,
  ProjectMilestone,
} from '../../../store/features/projects/projectsSlice';
import type { Ticket } from '../../../store/features/tickets/types';
import { convertFromUTC, getUserTimezone } from '../../../utils/timezone';

type ProjectDraft = {
  brief: string;
  definitionOfDone: string;
  stakeholderNotes: string;
  operatingRules: string;
  duplicatePreventionRules: string;
  startDate: string;
  endDate: string;
};

type MilestoneDraft = {
  title: string;
  startDate: string;
  endDate: string;
  outcome: string;
  definitionOfDone: string;
  notesBlockers: string;
};

type Props = {
  open: boolean;
  project: Project | null;
  organizationLabel?: string | null;
  teamLabel?: string | null;
  tickets?: Ticket[];
  viewerTimezone?: string | null;
  statusSummary?: ProjectStatusSummary | null;
  onClose: () => void;
  onSaveProject?: (patch: {
    brief?: string | null;
    definitionOfDone?: string | null;
    stakeholderNotes?: string | null;
    operatingRules?: string | null;
    duplicatePreventionRules?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  }) => Promise<void> | void;
  onSaveMilestone?: (
    milestoneId: string,
    patch: {
      title?: string;
      outcome?: string | null;
      definitionOfDone?: string | null;
      notesBlockers?: string | null;
      startDate?: string | null;
      endDate?: string | null;
    },
  ) => Promise<void> | void;
};

type DrawerTab = 'overview' | 'milestones';

export type UrgencyTone = 'low' | 'watch' | 'elevated' | 'high' | 'critical';

export type ProjectStatusSummary = {
  timezone: string;
  estimateHours: number;
  accumulatedHours: number;
  ringProgress: number;
  dueDateLabel: string;
  dueDateMeta: string;
  days: number | null;
  tone: UrgencyTone;
  label: string;
  detail: string;
  meter: number;
  totalMilestones: number;
  completedMilestones: number;
  remainingMilestones: number;
  totalTickets: number;
  completedTickets: number;
  remainingTickets: number;
  blockedTickets: number;
  overdueTickets: number;
  milestoneProgress: number;
  remainingWorkHours: number;
  forecastMode: 'delivery_only' | 'team' | 'contract' | 'team_contract';
  teamCapacityHoursPerContributor: number | null;
  teamContributorCount: number | null;
  teamQueuedHours: number | null;
  teamAssignedHours: number | null;
  teamUnassignedHours: number | null;
  teamHeadroomHours: number | null;
  contractServiceLabel: string | null;
  contractRangeHours: string | null;
  contractQueuedHours: number | null;
  contractRemainingHours: number | null;
  contractHeadroomHours: number | null;
  forecastSignals: string[];
};

export type ProjectCapacityForecastContext = {
  team?: {
    capacityHoursPerContributor?: number | null;
    contributorCount?: number | null;
    queuedMinutes?: number | null;
    assignedMinutes?: number | null;
    unassignedMinutes?: number | null;
  } | null;
  contract?: {
    serviceLabel?: string | null;
    contractedMinutesMin?: number | null;
    contractedMinutesMax?: number | null;
    remainingBillableMinutesMax?: number | null;
    plannedRemainingEstimateMinutes?: number | null;
  } | null;
};

const Backdrop = styled(motion.button)`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1200;
`;

const Sheet = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  width: clamp(420px, 72vw, 820px);
  height: 100dvh;
  display: grid;
  grid-template-rows: auto auto 1fr;
  overflow: hidden;
  z-index: 1201;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  color: #e8eaed;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
  box-shadow: -18px 0 48px rgba(0, 0, 0, 0.45);
  color-scheme: dark;

  @supports not (height: 100dvh) {
    height: 100vh;
  }

  @media (max-width: 860px) {
    width: 100vw;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const Header = styled.div`
  padding: 12px;
  display: grid;
  gap: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const HeaderTop = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  padding-right: 96px;
`;

const HeaderActions = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd2e1;
  display: grid;
  place-items: center;
  font-size: 18px;
  cursor: pointer;
`;

const SaveButton = styled.button`
  min-height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(224, 161, 94, 0.38);
  background: rgba(224, 161, 94, 0.14);
  color: #f8ebd6;
  font-size: 0.74rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const TabsRow = styled.nav`
  display: flex;
  gap: 2px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const TabButton = styled.button<{ $active?: boolean }>`
  min-height: 38px;
  margin-bottom: -1px;
  border-radius: 12px 12px 0 0;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)'};
  border-bottom-color: transparent;
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
  color: ${({ $active }) => ($active ? '#f8ebd6' : '#cbd2e1')};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.02em;
  padding: 0 14px;
  cursor: pointer;
  white-space: nowrap;
`;

const Body = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 14px 12px 18px;
  display: grid;
  gap: 18px;
  align-content: start;
  align-items: start;
`;

const Section = styled.section`
  display: grid;
  gap: 10px;
  align-content: start;
  align-self: start;

  & + & {
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

const flatField = css`
  width: 100%;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #e8eaed;
  outline: none;
  box-shadow: none;
  font-weight: 700;

  &:hover:not(:focus-visible) {
    border-style: dashed;
    border-color: rgba(255, 255, 255, 0.28);
  }

  &:focus-visible {
    background: rgba(255, 255, 255, 0.03);
    border-style: solid;
    border-color: transparent;
    box-shadow: inset 0 0 0 1px rgba(138, 180, 248, 0.75);
    outline: 2px solid rgba(138, 180, 248, 0.35);
    outline-offset: 1px;
  }
`;

const TextArea = styled.textarea`
  ${flatField}
  min-height: 88px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;
`;

const TextInput = styled.input`
  ${flatField}
  min-height: 40px;
  padding: 8px 12px;
  font-size: 13px;
`;

const Card = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
`;

const Label = styled.div`
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Badge = styled.span<{ $tone: UrgencyTone }>`
  min-height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.16px;
  text-transform: uppercase;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'critical'
        ? 'rgba(248,113,113,.4)'
        : $tone === 'high'
          ? 'rgba(251,146,60,.4)'
          : $tone === 'elevated'
            ? 'rgba(250,204,21,.36)'
            : $tone === 'watch'
              ? 'rgba(96,165,250,.34)'
              : 'rgba(110,214,171,.34)'};
  background: ${({ $tone }) =>
    $tone === 'critical'
      ? 'rgba(248,113,113,.14)'
      : $tone === 'high'
        ? 'rgba(251,146,60,.14)'
        : $tone === 'elevated'
          ? 'rgba(250,204,21,.12)'
          : $tone === 'watch'
            ? 'rgba(96,165,250,.12)'
            : 'rgba(110,214,171,.12)'};
  color: ${({ $tone }) =>
    $tone === 'critical'
      ? '#fecaca'
      : $tone === 'high'
        ? '#fed7aa'
        : $tone === 'elevated'
          ? '#fde68a'
          : $tone === 'watch'
            ? '#bfdbfe'
            : '#d1fae5'};
`;

const EmptyState = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  padding: 18px 0;
`;

const MilestonesLayout = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(220px, 260px) minmax(0, 1fr);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const MilestoneList = styled.div`
  display: grid;
  gap: 8px;
  align-content: start;
`;

const MilestoneListItem = styled.button<{ $active?: boolean }>`
  border: 1px dashed
    ${({ $active }) =>
      $active ? 'rgba(224, 161, 94, 0.62)' : 'rgba(255, 255, 255, 0.18)'};
  border-radius: 12px;
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.08)' : 'transparent'};
  padding: 10px 12px;
  display: grid;
  gap: 5px;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.42);
  }
`;

const MilestoneListTitle = styled.div`
  color: #e8eaed;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
`;

const MilestoneListMeta = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 11px;
  font-weight: 700;
`;

const DAY_MS = 24 * 60 * 60 * 1000;
const COMPLETE_STATUSES = new Set(['resolved', 'closed']);

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function normalizeText(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function asDate(value: unknown) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'object') {
    const record = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof record.toDate === 'function') {
      const date = record.toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const seconds =
      typeof record.seconds === 'number'
        ? record.seconds
        : typeof record._seconds === 'number'
          ? record._seconds
          : null;
    if (seconds !== null) {
      const date = new Date(seconds * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }
  return null;
}

function gradientForTone(tone: UrgencyTone) {
  if (tone === 'critical') return 'linear-gradient(90deg, rgba(248,113,113,.85), rgba(251,146,60,.92))';
  if (tone === 'high') return 'linear-gradient(90deg, rgba(251,146,60,.82), rgba(250,204,21,.9))';
  if (tone === 'elevated') return 'linear-gradient(90deg, rgba(250,204,21,.8), rgba(244,208,63,.86))';
  if (tone === 'watch') return 'linear-gradient(90deg, rgba(96,165,250,.76), rgba(125,211,252,.82))';
  return 'linear-gradient(90deg, rgba(110,214,171,.78), rgba(74,222,128,.84))';
}

function toDayKey(value: unknown, timezone: string) {
  const date = asDate(value);
  if (!date) return null;
  try {
    return convertFromUTC(date, timezone).localDate;
  } catch {
    return null;
  }
}

function dayDiff(fromDayKey: string, toDayKey: string) {
  const from = Date.parse(`${fromDayKey}T00:00:00Z`);
  const to = Date.parse(`${toDayKey}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.ceil((to - from) / DAY_MS);
}

function toMetaDate(value: unknown, timezone: string, fallback = '--') {
  const date = asDate(value);
  if (!date) return fallback;
  try {
    const { localDate } = convertFromUTC(date, timezone);
    const [year, month, day] = localDate.split('-');
    if (!year || !month || !day) return fallback;
    return `${month}/${day}/${year}`;
  } catch {
    return fallback;
  }
}

function toDateInputValue(value: unknown, timezone: string) {
  const date = asDate(value);
  if (!date) return '';
  try {
    return convertFromUTC(date, timezone).localDate;
  } catch {
    return '';
  }
}

function toMilestoneDateRangeLabel(milestone: ProjectMilestone, timezone: string) {
  const start = toMetaDate(milestone.plannedStartAt, timezone, '--/--');
  const end = toMetaDate(milestone.plannedEndAt, timezone, '--/--');
  return `From ${start.slice(0, 5)} - To ${end.slice(0, 5)}`;
}

function isTicketComplete(status: unknown) {
  return COMPLETE_STATUSES.has(String(status ?? '').trim());
}

function toNonNegativeNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function formatHoursValue(minutes: number) {
  return Math.max(0, Math.round(minutes / 60));
}

function formatHoursCompact(minutes: number) {
  const hours = minutes / 60;
  return hours >= 10 ? `${Math.round(hours)}h` : `${hours.toFixed(1).replace(/\.0$/, '')}h`;
}

function buildProjectDraft(project: Project | null, timezone: string): ProjectDraft {
  return {
    brief: normalizeText(project?.brief),
    definitionOfDone: normalizeText(project?.definitionOfDone),
    stakeholderNotes: normalizeText(project?.stakeholderNotes),
    operatingRules: normalizeText(project?.operatingRules),
    duplicatePreventionRules: normalizeText(project?.duplicatePreventionRules),
    startDate: toDateInputValue(project?.plannedStartAt, timezone),
    endDate: toDateInputValue(project?.plannedEndAt, timezone),
  };
}

function buildMilestoneDraft(
  milestone: ProjectMilestone | null | undefined,
  timezone: string,
): MilestoneDraft {
  return {
    title: normalizeText(milestone?.title),
    startDate: toDateInputValue(milestone?.plannedStartAt, timezone),
    endDate: toDateInputValue(milestone?.plannedEndAt, timezone),
    outcome: normalizeText(milestone?.outcome),
    definitionOfDone: normalizeText(milestone?.definitionOfDone),
    notesBlockers: normalizeText(milestone?.notesBlockers),
  };
}

function projectDraftsEqual(a: ProjectDraft, b: ProjectDraft) {
  return (
    a.brief === b.brief &&
    a.definitionOfDone === b.definitionOfDone &&
    a.stakeholderNotes === b.stakeholderNotes &&
    a.operatingRules === b.operatingRules &&
    a.duplicatePreventionRules === b.duplicatePreventionRules &&
    a.startDate === b.startDate &&
    a.endDate === b.endDate
  );
}

function milestoneDraftsEqual(a: MilestoneDraft, b: MilestoneDraft) {
  return (
    a.title === b.title &&
    a.startDate === b.startDate &&
    a.endDate === b.endDate &&
    a.outcome === b.outcome &&
    a.definitionOfDone === b.definitionOfDone &&
    a.notesBlockers === b.notesBlockers
  );
}

export function buildProjectStatusSummary(
  project: Project | null,
  tickets: Ticket[],
  viewerTimezone: string | null | undefined,
  capacityContext?: ProjectCapacityForecastContext | null,
): ProjectStatusSummary {
  const timezone =
    String(project?.timezone ?? viewerTimezone ?? getUserTimezone() ?? 'UTC').trim() || 'UTC';
  const milestones = Array.isArray(project?.milestones) ? project.milestones : [];
  const totalTickets = tickets.length;
  const completedTickets = tickets.filter((ticket) => isTicketComplete(ticket.status)).length;
  const remainingTickets = Math.max(totalTickets - completedTickets, 0);
  const blockedTickets = tickets.filter(
    (ticket) =>
      !isTicketComplete(ticket.status) && String(ticket.status ?? '').trim() === 'blocked',
  ).length;
  const totalEstimateMinutes = tickets.reduce(
    (sum, ticket) =>
      sum +
      Math.max(
        toNonNegativeNumber(ticket.estimatedMinutes),
        toNonNegativeNumber(ticket.remainingEstimateMinutes),
      ),
    0,
  );
  const remainingEstimateMinutes = tickets.reduce((sum, ticket) => {
    if (isTicketComplete(ticket.status)) return sum;
    return (
      sum +
      Math.max(
        toNonNegativeNumber(ticket.remainingEstimateMinutes),
        toNonNegativeNumber(ticket.estimatedMinutes),
      )
    );
  }, 0);
  const accumulatedMinutes = tickets.reduce(
    (sum, ticket) => sum + toNonNegativeNumber(ticket.actualActiveMinutes),
    0,
  );
  const todayDay = toDayKey(new Date(), timezone);
  const overdueTickets = tickets.filter((ticket) => {
    if (isTicketComplete(ticket.status)) return false;
    const dueDay = toDayKey(ticket.plannedEndAt, timezone);
    if (!todayDay || !dueDay) return false;
    const diff = dayDiff(todayDay, dueDay);
    return diff !== null && diff < 0;
  }).length;
  const latestMilestoneDueAt = milestones.reduce<unknown>((latest, milestone) => {
    const current = asDate(milestone.plannedEndAt);
    if (!current) return latest;
    const existing = asDate(latest);
    if (!existing || current.getTime() > existing.getTime()) return milestone.plannedEndAt;
    return latest;
  }, null);
  const dueAt = project?.plannedEndAt ?? latestMilestoneDueAt ?? null;
  const dueDateDerivedFromMilestones = !project?.plannedEndAt && Boolean(latestMilestoneDueAt);
  const dueDay = dueAt ? toDayKey(dueAt, timezone) : null;
  const days = todayDay && dueDay ? dayDiff(todayDay, dueDay) : null;
  const milestoneCompletion = milestones.map((milestone) => {
    const linkedTickets = tickets.filter(
      (ticket) => String(ticket.milestoneId ?? '').trim() === milestone.id,
    );
    return {
      complete:
        linkedTickets.length > 0 &&
        linkedTickets.every((ticket) => isTicketComplete(ticket.status)),
    };
  });
  const totalMilestones = milestoneCompletion.length;
  const completedMilestones = milestoneCompletion.filter((entry) => entry.complete).length;
  const remainingMilestones = Math.max(totalMilestones - completedMilestones, 0);
  const milestoneProgress = totalMilestones > 0 ? completedMilestones / totalMilestones : 0;
  const estimateHours = formatHoursValue(totalEstimateMinutes);
  const accumulatedHours = formatHoursValue(accumulatedMinutes);
  const remainingWorkHours = formatHoursValue(remainingEstimateMinutes);
  const ringProgress =
    totalEstimateMinutes > 0 ? clamp(accumulatedMinutes / totalEstimateMinutes, 0, 1) : 0;

  const teamCapacityHoursPerContributorRaw =
    capacityContext?.team?.capacityHoursPerContributor ?? null;
  const teamCapacityHoursPerContributor =
    typeof teamCapacityHoursPerContributorRaw === 'number' &&
    Number.isFinite(teamCapacityHoursPerContributorRaw) &&
    teamCapacityHoursPerContributorRaw > 0
      ? Math.round(teamCapacityHoursPerContributorRaw)
      : null;
  const teamContributorCountRaw = capacityContext?.team?.contributorCount ?? null;
  const teamContributorCount =
    typeof teamContributorCountRaw === 'number' &&
    Number.isFinite(teamContributorCountRaw) &&
    teamContributorCountRaw > 0
      ? Math.round(teamContributorCountRaw)
      : null;
  const teamQueuedMinutes = toNonNegativeNumber(capacityContext?.team?.queuedMinutes);
  const teamAssignedMinutes = toNonNegativeNumber(capacityContext?.team?.assignedMinutes);
  const teamUnassignedMinutes = toNonNegativeNumber(capacityContext?.team?.unassignedMinutes);
  const teamQueuedHours =
    teamCapacityHoursPerContributor !== null || teamQueuedMinutes > 0
      ? formatHoursValue(teamQueuedMinutes)
      : null;
  const teamAssignedHours =
    teamCapacityHoursPerContributor !== null || teamAssignedMinutes > 0
      ? formatHoursValue(teamAssignedMinutes)
      : null;
  const teamUnassignedHours =
    teamCapacityHoursPerContributor !== null || teamUnassignedMinutes > 0
      ? formatHoursValue(teamUnassignedMinutes)
      : null;
  const teamTotalCapacityMinutes =
    teamCapacityHoursPerContributor !== null && teamContributorCount !== null
      ? teamCapacityHoursPerContributor * teamContributorCount * 60
      : null;
  const teamHeadroomMinutes =
    teamTotalCapacityMinutes !== null ? teamTotalCapacityMinutes - teamQueuedMinutes : null;
  const teamHeadroomHours =
    teamHeadroomMinutes !== null ? formatHoursValue(Math.max(teamHeadroomMinutes, 0)) : null;

  const contractServiceLabel =
    typeof capacityContext?.contract?.serviceLabel === 'string' &&
    capacityContext.contract.serviceLabel.trim()
      ? capacityContext.contract.serviceLabel.trim()
      : null;
  const contractRangeMinMinutes = toNonNegativeNumber(
    capacityContext?.contract?.contractedMinutesMin,
  );
  const contractRangeMaxMinutes = toNonNegativeNumber(
    capacityContext?.contract?.contractedMinutesMax,
  );
  const contractRemainingMinutes = toNonNegativeNumber(
    capacityContext?.contract?.remainingBillableMinutesMax,
  );
  const contractQueuedMinutes = toNonNegativeNumber(
    capacityContext?.contract?.plannedRemainingEstimateMinutes,
  );
  const contractHeadroomMinutes =
    contractRemainingMinutes > 0 || contractQueuedMinutes > 0
      ? contractRemainingMinutes - contractQueuedMinutes
      : null;
  const contractRemainingHours =
    contractRemainingMinutes > 0 || contractQueuedMinutes > 0
      ? formatHoursValue(contractRemainingMinutes)
      : null;
  const contractQueuedHours =
    contractRemainingMinutes > 0 || contractQueuedMinutes > 0
      ? formatHoursValue(contractQueuedMinutes)
      : null;
  const contractHeadroomHours =
    contractHeadroomMinutes !== null
      ? formatHoursValue(Math.max(contractHeadroomMinutes, 0))
      : null;
  const contractRangeHours =
    contractRangeMinMinutes > 0 || contractRangeMaxMinutes > 0
      ? `${
          contractRangeMinMinutes > 0 ? formatHoursCompact(contractRangeMinMinutes) : '--'
        } - ${
          contractRangeMaxMinutes > 0 ? formatHoursCompact(contractRangeMaxMinutes) : '--'
        }`
      : null;

  const forecastMode =
    teamTotalCapacityMinutes !== null && (contractRemainingMinutes > 0 || contractQueuedMinutes > 0)
      ? 'team_contract'
      : teamTotalCapacityMinutes !== null
        ? 'team'
        : contractRemainingMinutes > 0 || contractQueuedMinutes > 0
          ? 'contract'
          : 'delivery_only';
  const forecastSignals: string[] = [];

  const buildBaseSummary = (overrides: {
    dueDateLabel: string;
    dueDateMeta: string;
    tone: UrgencyTone;
    label: string;
    detail: string;
    meter: number;
  }): ProjectStatusSummary => ({
    timezone,
    estimateHours,
    accumulatedHours,
    ringProgress,
    dueDateLabel: overrides.dueDateLabel,
    dueDateMeta: overrides.dueDateMeta,
    days,
    tone: overrides.tone,
    label: overrides.label,
    detail: overrides.detail,
    meter: overrides.meter,
    totalMilestones,
    completedMilestones,
    remainingMilestones,
    totalTickets,
    completedTickets,
    remainingTickets,
    blockedTickets,
    overdueTickets,
    milestoneProgress,
    remainingWorkHours,
    forecastMode,
    teamCapacityHoursPerContributor,
    teamContributorCount,
    teamQueuedHours,
    teamAssignedHours,
    teamUnassignedHours,
    teamHeadroomHours,
    contractServiceLabel,
    contractRangeHours,
    contractQueuedHours,
    contractRemainingHours,
    contractHeadroomHours,
    forecastSignals,
  });

  if (totalTickets === 0) {
    return buildBaseSummary({
      dueDateLabel: dueAt ? toMetaDate(dueAt, timezone) : '--',
      dueDateMeta: dueAt
        ? dueDateDerivedFromMilestones
          ? 'Using the latest milestone end date until a project due date is set.'
          : 'Using the project due date.'
        : 'Set a project due date or milestone end date to evaluate urgency.',
      tone: dueAt ? 'watch' : 'low',
      label: dueAt ? 'No work planned' : 'No due date',
      detail: dueAt
        ? 'This project does not have any tickets yet, so urgency cannot be evaluated from execution work.'
        : 'Add a project due date and planned work before using urgency as a delivery signal.',
      meter: dueAt ? 0.18 : 0.08,
    });
  }

  if (remainingTickets === 0 && remainingMilestones === 0) {
    return buildBaseSummary({
      dueDateLabel: dueAt ? toMetaDate(dueAt, timezone) : '--',
      dueDateMeta: dueAt
        ? dueDateDerivedFromMilestones
          ? 'Using the latest milestone end date until a project due date is set.'
          : 'Using the project due date.'
        : 'No due date set.',
      tone: 'low',
      label: 'Complete',
      detail: `All ${totalTickets} project tickets are complete${
        totalMilestones > 0 ? ` and all ${totalMilestones} milestones are closed.` : '.'
      }`,
      meter: 0.08,
    });
  }

  if (days === null) {
    return buildBaseSummary({
      dueDateLabel: '--',
      dueDateMeta: 'Set a project due date or milestone end date to make urgency meaningful.',
      tone: 'watch',
      label: 'No due date',
      detail: `${remainingTickets} ticket${remainingTickets === 1 ? '' : 's'} remain, but the project does not have a scheduled end date yet.`,
      meter: 0.34,
    });
  }

  const remainingTicketRatio = totalTickets > 0 ? remainingTickets / totalTickets : 0;
  const remainingEstimateRatio =
    totalEstimateMinutes > 0 ? remainingEstimateMinutes / totalEstimateMinutes : remainingTicketRatio;
  const workPressure = Math.max(remainingTicketRatio, remainingEstimateRatio);
  const milestonePressure =
    totalMilestones > 0 ? remainingMilestones / totalMilestones : remainingTicketRatio;
  const blockedRatio = remainingTickets > 0 ? blockedTickets / remainingTickets : 0;
  const overdueRatio = remainingTickets > 0 ? overdueTickets / remainingTickets : 0;
  const teamPressure =
    teamTotalCapacityMinutes !== null && teamTotalCapacityMinutes > 0
      ? clamp(teamQueuedMinutes / teamTotalCapacityMinutes, 0, 1)
      : null;
  const contractPressure =
    contractRemainingMinutes > 0
      ? clamp(contractQueuedMinutes / contractRemainingMinutes, 0, 1)
      : null;
  const daysPressure =
    days < 0
      ? 1
      : days <= 3
        ? 0.94
        : days <= 7
          ? 0.8
          : days <= 14
            ? 0.64
            : days <= 21
              ? 0.48
              : days <= 30
                ? 0.34
                : 0.18;
  const scoreInputs = [
    { weight: 0.28, value: daysPressure },
    { weight: 0.18, value: workPressure },
    { weight: 0.08, value: milestonePressure },
    { weight: 0.08, value: blockedRatio },
    { weight: 0.08, value: overdueRatio },
    ...(teamPressure !== null ? [{ weight: 0.18, value: teamPressure }] : []),
    ...(contractPressure !== null ? [{ weight: 0.12, value: contractPressure }] : []),
  ];
  const totalWeight = scoreInputs.reduce((sum, item) => sum + item.weight, 0);
  const score = clamp(
    scoreInputs.reduce((sum, item) => sum + item.value * item.weight, 0) /
      (totalWeight || 1),
    0,
    1,
  );

  let tone: UrgencyTone = 'low';
  let label = 'On track';
  if (days < 0) {
    tone = 'critical';
    label = 'Overdue';
  } else if (contractHeadroomMinutes !== null && contractHeadroomMinutes < 0) {
    tone = 'critical';
    label = 'Contract capped';
  } else if (teamHeadroomMinutes !== null && teamHeadroomMinutes < 0) {
    tone = 'critical';
    label = 'Over capacity';
  } else if (score >= 0.82) {
    tone = 'critical';
    label = 'Critical';
  } else if (score >= 0.68) {
    tone = 'high';
    label = 'High';
  } else if (score >= 0.5) {
    tone = 'elevated';
    label = 'Elevated';
  } else if (score >= 0.32) {
    tone = 'watch';
    label = 'Watch';
  }

  let detail = `${remainingTickets} ticket${remainingTickets === 1 ? '' : 's'} remain`;
  if (remainingMilestones > 0) {
    detail += ` across ${remainingMilestones} milestone${remainingMilestones === 1 ? '' : 's'}`;
  }
  detail += ` with ${Math.max(days, 0)} day${Math.abs(days) === 1 ? '' : 's'} left.`;
  if (days < 0) {
    detail = `${remainingTickets} ticket${remainingTickets === 1 ? '' : 's'} and ${remainingMilestones} milestone${remainingMilestones === 1 ? '' : 's'} still remain after the project due date.`;
  } else if (overdueTickets > 0) {
    detail = `${overdueTickets} ticket${overdueTickets === 1 ? '' : 's'} are already past planned dates before the project due date, with ${days} day${days === 1 ? '' : 's'} left overall.`;
  } else if (blockedTickets > 0 && days <= 14) {
    detail = `${blockedTickets} blocked ticket${blockedTickets === 1 ? '' : 's'} are compressing the remaining ${days} day${days === 1 ? '' : 's'} in the project window.`;
  } else if (remainingEstimateMinutes > 0) {
    detail += ` Approximately ${formatHoursCompact(remainingEstimateMinutes)} of ticket work still remain.`;
  }
  if (teamHeadroomMinutes !== null) {
    if (teamHeadroomMinutes < 0) {
      forecastSignals.push(
        `Team queue is ${formatHoursCompact(Math.abs(teamHeadroomMinutes))} over current staffed capacity.`,
      );
    } else {
      forecastSignals.push(
        `Team headroom is ${formatHoursCompact(teamHeadroomMinutes)} across ${
          teamContributorCount ?? 0
        } active contributor${teamContributorCount === 1 ? '' : 's'}.`,
      );
    }
  }
  if (contractHeadroomMinutes !== null) {
    if (contractHeadroomMinutes < 0) {
      forecastSignals.push(
        `${contractServiceLabel ?? 'Service contract'} is projected ${formatHoursCompact(
          Math.abs(contractHeadroomMinutes),
        )} past the monthly hard stop.`,
      );
    } else {
      forecastSignals.push(
        `${contractServiceLabel ?? 'Service contract'} has ${formatHoursCompact(
          contractHeadroomMinutes,
        )} of contract buffer left after queued work.`,
      );
    }
  }
  if (forecastSignals.length) {
    detail += ` ${forecastSignals[0]}`;
  }
  if (dueDateDerivedFromMilestones) {
    detail += ' The due date is currently being derived from the latest milestone end date.';
  }

  return buildBaseSummary({
    dueDateLabel: dueAt ? toMetaDate(dueAt, timezone) : '--',
    dueDateMeta: dueDateDerivedFromMilestones
      ? 'Using the latest milestone end date until a project due date is set.'
      : 'Using the project due date.',
    tone,
    label,
    detail,
    meter: Math.max(0.08, score),
  });
}

export default function ProjectDetailsDrawer({
  open,
  project,
  organizationLabel,
  teamLabel,
  tickets = [],
  viewerTimezone = null,
  statusSummary: cachedStatusSummary = null,
  onClose,
  onSaveProject,
  onSaveMilestone,
}: Props) {
  const [tab, setTab] = React.useState<DrawerTab>('overview');
  const [projectDraft, setProjectDraft] = React.useState<ProjectDraft>(() =>
    buildProjectDraft(null, viewerTimezone || getUserTimezone() || 'UTC'),
  );
  const [projectSaving, setProjectSaving] = React.useState(false);
  const [selectedMilestoneId, setSelectedMilestoneId] = React.useState<string | null>(null);
  const [milestoneDrafts, setMilestoneDrafts] = React.useState<Record<string, MilestoneDraft>>({});
  const [milestoneSavingId, setMilestoneSavingId] = React.useState<string | null>(null);

  const timezone =
    String(project?.timezone ?? viewerTimezone ?? getUserTimezone() ?? 'UTC').trim() || 'UTC';
  const milestones = React.useMemo(
    () => (Array.isArray(project?.milestones) ? project.milestones : []),
    [project?.milestones],
  );

  React.useEffect(() => {
    if (!open) return;
    setProjectDraft(buildProjectDraft(project, timezone));
    setMilestoneDrafts(
      Object.fromEntries(
        milestones.map((milestone) => [milestone.id, buildMilestoneDraft(milestone, timezone)]),
      ),
    );
    setSelectedMilestoneId((current) => {
      if (current && milestones.some((milestone) => milestone.id === current)) return current;
      return milestones[0]?.id ?? null;
    });
    setProjectSaving(false);
    setMilestoneSavingId(null);
    setTab((current) => (current === 'milestones' && milestones.length === 0 ? 'overview' : current));
  }, [milestones, open, project, timezone]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  const statusSummary = React.useMemo(
    () => cachedStatusSummary ?? buildProjectStatusSummary(project, tickets, viewerTimezone),
    [cachedStatusSummary, project, tickets, viewerTimezone],
  );

  const projectBaseline = React.useMemo(
    () => buildProjectDraft(project, timezone),
    [project, timezone],
  );
  const projectDirty = React.useMemo(
    () => !projectDraftsEqual(projectDraft, projectBaseline),
    [projectBaseline, projectDraft],
  );

  const selectedMilestone = React.useMemo(
    () => milestones.find((milestone) => milestone.id === selectedMilestoneId) ?? null,
    [milestones, selectedMilestoneId],
  );
  const selectedMilestoneBaseline = React.useMemo(
    () => buildMilestoneDraft(selectedMilestone, timezone),
    [selectedMilestone, timezone],
  );
  const selectedMilestoneDraft = React.useMemo(() => {
    if (!selectedMilestone) return null;
    return milestoneDrafts[selectedMilestone.id] ?? buildMilestoneDraft(selectedMilestone, timezone);
  }, [milestoneDrafts, selectedMilestone, timezone]);
  const milestoneDirty = React.useMemo(() => {
    if (!selectedMilestone || !selectedMilestoneDraft) return false;
    return !milestoneDraftsEqual(selectedMilestoneDraft, selectedMilestoneBaseline);
  }, [selectedMilestone, selectedMilestoneBaseline, selectedMilestoneDraft]);

  const ringGradient = `conic-gradient(${
    statusSummary.accumulatedHours > statusSummary.estimateHours && statusSummary.estimateHours > 0
      ? 'rgba(248,113,113,.9)'
      : 'rgba(110,214,171,.94)'
  } 0deg, ${
    statusSummary.accumulatedHours > statusSummary.estimateHours && statusSummary.estimateHours > 0
      ? 'rgba(248,113,113,.9)'
      : 'rgba(110,214,171,.94)'
  } ${statusSummary.ringProgress * 360}deg, rgba(255,255,255,.08) ${
    statusSummary.ringProgress * 360
  }deg, rgba(255,255,255,.08) 360deg)`;

  const updateMilestoneDraft = React.useCallback(
    (milestoneId: string, patch: Partial<MilestoneDraft>) => {
      setMilestoneDrafts((current) => ({
        ...current,
        [milestoneId]: {
          ...(current[milestoneId] ??
            buildMilestoneDraft(
              milestones.find((entry) => entry.id === milestoneId) ?? null,
              timezone,
            )),
          ...patch,
        },
      }));
    },
    [milestones, timezone],
  );

  const handleSaveProject = React.useCallback(async () => {
    if (!project || !projectDirty || !onSaveProject) return;
    setProjectSaving(true);
    try {
      await onSaveProject({
        brief: projectDraft.brief.trim() || null,
        definitionOfDone: projectDraft.definitionOfDone.trim() || null,
        stakeholderNotes: projectDraft.stakeholderNotes.trim() || null,
        operatingRules: projectDraft.operatingRules.trim() || null,
        duplicatePreventionRules: projectDraft.duplicatePreventionRules.trim() || null,
        startDate: projectDraft.startDate || null,
        endDate: projectDraft.endDate || null,
      });
    } finally {
      setProjectSaving(false);
    }
  }, [onSaveProject, project, projectDirty, projectDraft]);

  const handleSaveMilestone = React.useCallback(async () => {
    if (!selectedMilestone || !selectedMilestoneDraft || !milestoneDirty || !onSaveMilestone) {
      return;
    }
    setMilestoneSavingId(selectedMilestone.id);
    try {
      await onSaveMilestone(selectedMilestone.id, {
        title: selectedMilestoneDraft.title.trim(),
        outcome: selectedMilestoneDraft.outcome.trim() || null,
        definitionOfDone: selectedMilestoneDraft.definitionOfDone.trim() || null,
        notesBlockers: selectedMilestoneDraft.notesBlockers.trim() || null,
        startDate: selectedMilestoneDraft.startDate || null,
        endDate: selectedMilestoneDraft.endDate || null,
      });
    } finally {
      setMilestoneSavingId(null);
    }
  }, [milestoneDirty, onSaveMilestone, selectedMilestone, selectedMilestoneDraft]);

  if (typeof document === 'undefined') return null;

  const activeSaveLabel =
    tab === 'overview'
      ? projectDirty
        ? projectSaving
          ? 'Saving...'
          : 'Save overview'
        : null
      : selectedMilestone && milestoneDirty
        ? milestoneSavingId === selectedMilestone.id
          ? 'Saving...'
          : 'Save milestone'
        : null;

  return createPortal(
    <AnimatePresence>
      {open && project ? (
        <>
          <Backdrop
            type="button"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-label="Close project details"
          />
          <Sheet
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            aria-label="Project details"
          >
            <Header>
              <HeaderTop>
                <div style={{ color: '#9aa4b2', fontSize: 11, fontWeight: 900, letterSpacing: '.18px', textTransform: 'uppercase' }}>
                  Project details
                </div>
                <div style={{ color: '#e8eaed', fontSize: '1.2rem', fontWeight: 900, lineHeight: 1.1 }}>
                  {project.name}
                </div>
                <div style={{ color: '#9aa4b2', fontSize: 12, fontWeight: 700, lineHeight: 1.45 }}>
                  {organizationLabel || 'No workspace linked'} / {teamLabel || 'No team linked'}
                </div>
                <HeaderActions>
                  {activeSaveLabel ? (
                    <SaveButton
                      type="button"
                      disabled={projectSaving || Boolean(milestoneSavingId)}
                      onClick={() => {
                        if (tab === 'overview') {
                          void handleSaveProject();
                          return;
                        }
                        void handleSaveMilestone();
                      }}
                    >
                      {activeSaveLabel}
                    </SaveButton>
                  ) : null}
                  <CloseButton type="button" onClick={onClose} aria-label="Close project details">
                    x
                  </CloseButton>
                </HeaderActions>
              </HeaderTop>
            </Header>

            <TabsRow aria-label="Project detail tabs">
              <TabButton type="button" $active={tab === 'overview'} onClick={() => setTab('overview')}>
                Overview
              </TabButton>
              <TabButton type="button" $active={tab === 'milestones'} onClick={() => setTab('milestones')}>
                Milestones
              </TabButton>
            </TabsRow>

            <Body>
              {tab === 'overview' ? (
                <>
                  <Section>
                    <div style={{ color: '#e8eaed', fontSize: 12, fontWeight: 900, letterSpacing: '.16px', textTransform: 'uppercase' }}>
                      Project status
                    </div>
                    <div style={{ color: 'rgba(186,198,224,.82)', fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
                      Quick planning pressure at a glance: ticket estimates, logged effort, real due-date urgency, and milestone progress.
                    </div>
                    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, .95fr)' }}>
                      <Card>
                        <div style={{ color: '#e8eaed', fontSize: 13, fontWeight: 900, letterSpacing: '.16px', textTransform: 'uppercase' }}>
                          Hours utilization
                        </div>
                        <div style={{ color: 'rgba(186,198,224,.82)', fontSize: 11, lineHeight: 1.4, fontWeight: 700 }}>
                          The outer ring uses the sum of ticket estimates and the active ring uses logged ticket effort already accumulated on the project.
                        </div>
                        <div style={{ display: 'grid', gap: 16, alignItems: 'center', gridTemplateColumns: 'auto minmax(0, 1fr)' }}>
                          <div style={{ width: 172, height: 172, borderRadius: 999, padding: 14, display: 'grid', placeItems: 'center', background: ringGradient, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.04), 0 12px 28px rgba(0,0,0,.18)' }}>
                            <div style={{ width: '100%', height: '100%', borderRadius: 999, background: 'linear-gradient(180deg, rgba(22,24,30,.98), rgba(18,20,25,.98))', display: 'grid', placeItems: 'center', padding: 18, textAlign: 'center', gap: 6 }}>
                              <div style={{ color: '#e8eaed', fontSize: 30, lineHeight: 1.02, fontWeight: 900 }}>
                                {statusSummary.estimateHours > 0
                                  ? `${statusSummary.accumulatedHours}h / ${statusSummary.estimateHours}h`
                                  : `${statusSummary.accumulatedHours}h`}
                              </div>
                              <div style={{ color: 'rgba(186,198,224,.82)', fontSize: 10, fontWeight: 800, lineHeight: 1.3, maxWidth: 96, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                                {statusSummary.estimateHours > 0 ? 'Logged / estimate' : 'No estimates yet'}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                            <div style={{ display: 'grid', gap: 4 }}>
                              <Label>Estimate hours</Label>
                              <div style={{ color: '#e8eaed', fontSize: 26, fontWeight: 900, lineHeight: 1 }}>
                                {statusSummary.estimateHours}
                              </div>
                              <div style={{ color: 'rgba(186,198,224,.72)', fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
                                Summed from current ticket estimates on the project.
                              </div>
                            </div>
                            <div style={{ display: 'grid', gap: 4 }}>
                              <Label>Accumulated hours</Label>
                              <div style={{ color: '#e8eaed', fontSize: 26, fontWeight: 900, lineHeight: 1 }}>
                                {statusSummary.accumulatedHours}
                              </div>
                              <div style={{ color: 'rgba(186,198,224,.72)', fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
                                Summed from active ticket time already logged.
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                      <div style={{ display: 'grid', gap: 12 }}>
                        <Card>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                            <div>
                              <div style={{ color: '#e8eaed', fontSize: 13, fontWeight: 900, letterSpacing: '.16px', textTransform: 'uppercase' }}>
                                Due date urgency
                              </div>
                              <div style={{ color: '#e8eaed', fontSize: 20, lineHeight: 1.1, fontWeight: 900 }}>
                                {statusSummary.days === null
                                  ? '--'
                                  : statusSummary.days < 0
                                    ? `${Math.abs(statusSummary.days)} day${Math.abs(statusSummary.days) === 1 ? '' : 's'} overdue`
                                    : `${statusSummary.days} day${statusSummary.days === 1 ? '' : 's'}`}
                              </div>
                            </div>
                            <Badge $tone={statusSummary.tone}>{statusSummary.label}</Badge>
                          </div>
                          <div style={{ display: 'grid', gap: 4 }}>
                            <Label>Target completion</Label>
                            <div style={{ color: '#e8eaed', fontSize: 15, fontWeight: 800 }}>{statusSummary.dueDateLabel}</div>
                            <div style={{ color: 'rgba(186,198,224,.72)', fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
                              {statusSummary.dueDateMeta}
                            </div>
                          </div>
                          <div style={{ width: '100%', height: 8, borderRadius: 999, background: 'rgba(255,255,255,.06)', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.max(6, Math.min(statusSummary.meter * 100, 100))}%`, height: '100%', borderRadius: 999, background: gradientForTone(statusSummary.tone) }} />
                          </div>
                          <div style={{ color: 'rgba(186,198,224,.82)', fontSize: 11, lineHeight: 1.4, fontWeight: 700 }}>
                            {statusSummary.detail}
                          </div>
                          {statusSummary.forecastMode !== 'delivery_only' ? (
                            <div
                              style={{
                                display: 'grid',
                                gap: 8,
                                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                              }}
                            >
                              {statusSummary.teamQueuedHours !== null ? (
                                <div style={{ display: 'grid', gap: 3 }}>
                                  <Label>Team queue</Label>
                                  <div style={{ color: '#e8eaed', fontSize: 16, fontWeight: 900 }}>
                                    {statusSummary.teamQueuedHours}h
                                  </div>
                                  <div style={{ color: 'rgba(186,198,224,.72)', fontSize: 11, fontWeight: 700, lineHeight: 1.35 }}>
                                    {statusSummary.teamHeadroomHours !== null
                                      ? `${statusSummary.teamHeadroomHours}h headroom across ${statusSummary.teamContributorCount ?? 0} contributor${statusSummary.teamContributorCount === 1 ? '' : 's'}`
                                      : 'Using team queue pressure only'}
                                  </div>
                                </div>
                              ) : null}
                              {statusSummary.contractRemainingHours !== null ||
                              statusSummary.contractQueuedHours !== null ? (
                                <div style={{ display: 'grid', gap: 3 }}>
                                  <Label>
                                    {statusSummary.contractServiceLabel
                                      ? `${statusSummary.contractServiceLabel} contract`
                                      : 'Contract headroom'}
                                  </Label>
                                  <div style={{ color: '#e8eaed', fontSize: 16, fontWeight: 900 }}>
                                    {statusSummary.contractHeadroomHours !== null
                                      ? `${statusSummary.contractHeadroomHours}h left`
                                      : '--'}
                                  </div>
                                  <div style={{ color: 'rgba(186,198,224,.72)', fontSize: 11, fontWeight: 700, lineHeight: 1.35 }}>
                                    {statusSummary.contractRangeHours
                                      ? `Range ${statusSummary.contractRangeHours} this cycle`
                                      : 'Comparing queued work against the contract hard stop'}
                                  </div>
                                </div>
                              ) : null}
                              <div style={{ display: 'grid', gap: 3 }}>
                                <Label>Work left</Label>
                                <div style={{ color: '#e8eaed', fontSize: 16, fontWeight: 900 }}>
                                  {statusSummary.remainingWorkHours}h
                                </div>
                                <div style={{ color: 'rgba(186,198,224,.72)', fontSize: 11, fontWeight: 700, lineHeight: 1.35 }}>
                                  Remaining estimate across open project tickets.
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </Card>
                        <Card>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                            <div>
                              <div style={{ color: '#e8eaed', fontSize: 13, fontWeight: 900, letterSpacing: '.16px', textTransform: 'uppercase' }}>
                                Milestone completion
                              </div>
                              <div style={{ color: '#e8eaed', fontSize: 20, lineHeight: 1.1, fontWeight: 900 }}>
                                {statusSummary.totalMilestones > 0 ? `${statusSummary.completedMilestones} / ${statusSummary.totalMilestones}` : '--'}
                              </div>
                            </div>
                            <Badge $tone={statusSummary.totalMilestones === 0 ? 'watch' : statusSummary.remainingMilestones === 0 ? 'low' : 'watch'}>
                              {statusSummary.totalMilestones === 0 ? 'No milestones' : statusSummary.remainingMilestones === 0 ? 'Complete' : `${statusSummary.remainingMilestones} left`}
                            </Badge>
                          </div>
                          <div style={{ width: '100%', height: 8, borderRadius: 999, background: 'rgba(255,255,255,.06)', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.max(6, Math.min(statusSummary.milestoneProgress * 100, 100))}%`, height: '100%', borderRadius: 999, background: gradientForTone('low') }} />
                          </div>
                          <div style={{ color: 'rgba(186,198,224,.82)', fontSize: 11, lineHeight: 1.4, fontWeight: 700 }}>
                            {statusSummary.totalMilestones > 0 ? `${statusSummary.completedTickets} of ${statusSummary.totalTickets} tickets are complete across ${statusSummary.totalMilestones} milestones.` : 'Add milestones in the Milestones tab to break larger project goals into smaller deliverables.'}
                          </div>
                        </Card>
                      </div>
                    </div>
                  </Section>
                  <Section>
                    <div style={{ color: '#e8eaed', fontSize: 12, fontWeight: 900, letterSpacing: '.16px', textTransform: 'uppercase' }}>
                      Project framing
                    </div>
                    <div style={{ color: 'rgba(186,198,224,.82)', fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
                      Capture the outcome, completion criteria, and planning context the project manager should work from before it starts creating tickets.
                    </div>
                    <FieldGrid>
                      <Field>
                        <Label>Planned start</Label>
                        <TextInput type="date" value={projectDraft.startDate} onChange={(event) => setProjectDraft((current) => ({ ...current, startDate: event.target.value }))} />
                      </Field>
                      <Field>
                        <Label>Planned end</Label>
                        <TextInput type="date" value={projectDraft.endDate} onChange={(event) => setProjectDraft((current) => ({ ...current, endDate: event.target.value }))} />
                      </Field>
                    </FieldGrid>
                    <Field>
                      <Label>Project brief</Label>
                      <TextArea value={projectDraft.brief} onChange={(event) => setProjectDraft((current) => ({ ...current, brief: event.target.value }))} placeholder="State the goal of the project, the core outcome, and what this work needs to achieve for the client." />
                    </Field>
                    <Field>
                      <Label>Definition of done</Label>
                      <TextArea value={projectDraft.definitionOfDone} onChange={(event) => setProjectDraft((current) => ({ ...current, definitionOfDone: event.target.value }))} placeholder="Describe the completion criteria, handoff expectations, and what must be true for the project to be considered complete." />
                    </Field>
                    <Field>
                      <Label>Stakeholder notes</Label>
                      <TextArea value={projectDraft.stakeholderNotes} onChange={(event) => setProjectDraft((current) => ({ ...current, stakeholderNotes: event.target.value }))} placeholder="Capture stakeholder preferences, client sensitivities, review expectations, and any constraints that should shape the project plan." />
                    </Field>
                  </Section>
                  <Section>
                    <div style={{ color: '#e8eaed', fontSize: 12, fontWeight: 900, letterSpacing: '.16px', textTransform: 'uppercase' }}>
                      Operating rules
                    </div>
                    <div style={{ color: 'rgba(186,198,224,.82)', fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
                      Define the planning rules that should shape ticket generation, especially around repeated work and duplicate prevention.
                    </div>
                    <FieldGrid>
                      <Field>
                        <Label>Operating rules</Label>
                        <TextArea value={projectDraft.operatingRules} onChange={(event) => setProjectDraft((current) => ({ ...current, operatingRules: event.target.value }))} placeholder="Add internal planning rules, workflow preferences, or project-management expectations for this project." />
                      </Field>
                      <Field>
                        <Label>Duplicate-prevention rules</Label>
                        <TextArea value={projectDraft.duplicatePreventionRules} onChange={(event) => setProjectDraft((current) => ({ ...current, duplicatePreventionRules: event.target.value }))} placeholder="Describe how repeated work should be handled and what the AI should treat as duplicate versus intentionally recurring." />
                      </Field>
                    </FieldGrid>
                  </Section>
                </>
              ) : (
                <Section>
                  <div style={{ color: '#e8eaed', fontSize: 12, fontWeight: 900, letterSpacing: '.16px', textTransform: 'uppercase' }}>
                    Milestones
                  </div>
                  <div style={{ color: 'rgba(186,198,224,.82)', fontSize: 11, fontWeight: 700, lineHeight: 1.4 }}>
                    Milestones are the smaller goals inside the larger project. Use this tab to add the planning context each milestone needs beyond the lightweight card shown in the projects panel.
                  </div>
                  {milestones.length === 0 ? (
                    <EmptyState>
                      No milestones exist for this project yet. Create the first milestone from the projects panel, then return here to add its deliverable, definition of done, and blockers.
                    </EmptyState>
                  ) : (
                    <MilestonesLayout>
                      <MilestoneList>
                        {milestones.map((milestone) => (
                          <MilestoneListItem key={milestone.id} type="button" $active={selectedMilestoneId === milestone.id} onClick={() => setSelectedMilestoneId(milestone.id)}>
                            <MilestoneListTitle>{milestone.title}</MilestoneListTitle>
                            <MilestoneListMeta>{toMilestoneDateRangeLabel(milestone, timezone)}</MilestoneListMeta>
                          </MilestoneListItem>
                        ))}
                      </MilestoneList>
                      {selectedMilestone && selectedMilestoneDraft ? (
                        <Card>
                          <Field>
                            <Label>Milestone title</Label>
                            <TextInput value={selectedMilestoneDraft.title} onChange={(event) => updateMilestoneDraft(selectedMilestone.id, { title: event.target.value })} placeholder="Name the milestone" />
                          </Field>
                          <FieldGrid>
                            <Field>
                              <Label>Planned start</Label>
                              <TextInput type="date" value={selectedMilestoneDraft.startDate} onChange={(event) => updateMilestoneDraft(selectedMilestone.id, { startDate: event.target.value })} />
                            </Field>
                            <Field>
                              <Label>Planned end</Label>
                              <TextInput type="date" value={selectedMilestoneDraft.endDate} onChange={(event) => updateMilestoneDraft(selectedMilestone.id, { endDate: event.target.value })} />
                            </Field>
                          </FieldGrid>
                          <Field>
                            <Label>Deliverable / outcome</Label>
                            <TextArea value={selectedMilestoneDraft.outcome} onChange={(event) => updateMilestoneDraft(selectedMilestone.id, { outcome: event.target.value })} placeholder="Describe what should exist or be achieved when this milestone is complete." />
                          </Field>
                          <Field>
                            <Label>Definition of done</Label>
                            <TextArea value={selectedMilestoneDraft.definitionOfDone} onChange={(event) => updateMilestoneDraft(selectedMilestone.id, { definitionOfDone: event.target.value })} placeholder="Capture the milestone-specific completion criteria, QA expectations, and handoff requirements." />
                          </Field>
                          <Field>
                            <Label>Notes / blockers</Label>
                            <TextArea value={selectedMilestoneDraft.notesBlockers} onChange={(event) => updateMilestoneDraft(selectedMilestone.id, { notesBlockers: event.target.value })} placeholder="Add any known blockers, dependencies, or internal notes that should shape how this milestone is planned." />
                          </Field>
                        </Card>
                      ) : null}
                    </MilestonesLayout>
                  )}
                </Section>
              )}
            </Body>
          </Sheet>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
